import * as React from 'react'
import {
  reduxForm,
  InjectedFormProps,
  formValueSelector,
  change
} from 'redux-form'
import { FormGroup, Button, ControlLabel, FormControl } from 'react-bootstrap'
import * as moment from 'moment'
import memoize from 'lodash.memoize'

import { FORM_LOAN_PAYMENT } from '../../constants/Form'
import {
  LOAN_PAYMENT_FORM_AMOUNT,
  LOAN_PAYMENT_FORM_DESCRIPTION,
  LOAN_PAYMENT_FORM_PAYED_ON,
  LOAN_PAYMENT_FORM_PAYERS
} from '../../constants/LoanPaymentFormFieldName'
import { InputControlField } from '../../components/form/InputControl'
import { DatepickerControlField } from '../../components/form/DatepickerControl'
import {
  SelectOption,
  SelectControlField
} from '../../components/form/SelectControl'
import { isRequired, isNumber } from '../../utils/validators'
import { LoanQueryResult } from '../../components/queries/LoanQuery'
import { MeQueryResult } from '../../components/queries/MeQuery'
import { connect } from 'react-redux'
import { StoreState } from '../../reducers'
import { GetProps } from '../../types'
import { calculateRequiredPayment } from '../../utils/transactions'
import { Dispatch } from 'redux'
import { AnyAction } from 'redux'
import { BASIC } from '../../constants/DateFormat'
import { moneyFormat } from '../../utils/format'

export interface LoanPaymentFormData {
  amount: string
  description: string
  payedOn: moment.Moment
  payers: SelectOption[]
}

type Loan = Exclude<LoanQueryResult['loan'], undefined | null>
type User = Exclude<MeQueryResult['user'], undefined | null>

export interface LoanPaymentFormOwnProps {
  loan: Loan
  user: User
}

export interface LoanPaymentFormStateProps {
  amount?: string
  payedOn?: moment.Moment
}

export interface LoanPaymentFormDispatchProps {
  changeAmount: (value: string) => void
}

type CombinedProps = LoanPaymentFormOwnProps &
  LoanPaymentFormStateProps &
  LoanPaymentFormDispatchProps

export type LoanPaymentFormProps = InjectedFormProps<
  LoanPaymentFormData,
  CombinedProps
> &
  CombinedProps

const atLeastOne = value =>
  value && value.length === 0
    ? 'Je třeba vybrat alespoň jednoho uživatele'
    : undefined

const calcAmort = (loan: Loan, date: moment.Moment, amount: number) => {
  const interest = (loan.state.interest || 0) / 100
  const days = date.diff(moment(loan.startedOn), 'day')
  const principal = amount / (1 + days * interest / 365)
  const amort = days * principal * interest / 365

  return Math.round(amort * 100) / 100
}

const isPositive = value =>
  value && Number(value) <= 0
    ? 'Částka musí být větší, nebo rovna 0'
    : undefined

// Need to memoize, because redux-form fields get remounted, when validators change
const makeAmountValidator = memoize(requiredPayment => value =>
  value && Number(value) > requiredPayment + 0.01
    ? 'Tato částka je příliš vysoká'
    : undefined
)

const makeFilterDate = memoize(startedOn => now => (date: moment.Moment) =>
  (date.isBefore(now) || date.isSame(now)) &&
  (date.isAfter(startedOn) || date.isSame(startedOn))
)

const getRequiredPayment = memoize(loan =>
  memoize(payedOn => calculateRequiredPayment(loan, payedOn || moment()))
)

const LoanPaymentForm: React.SFC<LoanPaymentFormProps> = ({
  handleSubmit,
  loan,
  amount,
  payedOn,
  changeAmount
}) => {
  const now = moment()
  const startedOn = moment(loan.startedOn)
  const filterDate = makeFilterDate(startedOn)(now)
  const requiredPayment = getRequiredPayment(loan)(payedOn)
  const isValidAmount = makeAmountValidator(requiredPayment)

  return (
    <form onSubmit={handleSubmit}>
      <SelectControlField
        name={LOAN_PAYMENT_FORM_PAYERS}
        label="Splátci"
        options={loan.debtors.map(debtor => ({
          value: debtor.id,
          label: debtor.name || ''
        }))}
        validate={[isRequired, atLeastOne]}
        multi={true}
      />
      <InputControlField
        label="Způsob vrácení"
        name={LOAN_PAYMENT_FORM_DESCRIPTION}
        type="text"
        placeholder="Způsob vrácení"
      />
      Částka potřebná ke splacení dluhu ke dni {(payedOn || now).format(BASIC)}:{' '}
      <a
        href="#"
        className="link"
        onClick={() => changeAmount(String(requiredPayment))}
        id="required-payment"
      >
        {moneyFormat(requiredPayment)}
      </a>
      <InputControlField
        label="Částka"
        name={LOAN_PAYMENT_FORM_AMOUNT}
        type="number"
        placeholder="Částka"
        validate={[isRequired, isNumber, isValidAmount, isPositive]}
      />
      {loan.interval == null && (
        <FormGroup>
          <ControlLabel>Z toho úroky</ControlLabel>
          <FormControl
            type="text"
            disabled={true}
            value={calcAmort(loan, payedOn || now, Number(amount) || 0)}
          />
        </FormGroup>
      )}
      <DatepickerControlField
        label="Dne"
        className="form-control"
        dateFormat={BASIC}
        placeholderText={now.format(BASIC)}
        name={LOAN_PAYMENT_FORM_PAYED_ON}
        filterDate={filterDate}
      />
      <FormGroup>
        <Button id="pay-loan" bsStyle="primary" type="submit">
          Uložit
        </Button>
      </FormGroup>
    </form>
  )
}

const formSelector = formValueSelector(FORM_LOAN_PAYMENT)

const connector = connect(
  (state: StoreState, props: LoanPaymentFormOwnProps) => {
    const amount = formSelector(state, LOAN_PAYMENT_FORM_AMOUNT)
    const payedOn = formSelector(state, LOAN_PAYMENT_FORM_PAYED_ON)

    return {
      amount,
      payedOn,
      initialValues: {
        payers: [
          {
            value: props.user.id,
            label: props.user.name
          }
        ]
      }
    }
  },
  (dispatch: Dispatch<AnyAction>, props: LoanPaymentFormOwnProps) => {
    return {
      changeAmount: value =>
        dispatch(change(FORM_LOAN_PAYMENT, LOAN_PAYMENT_FORM_AMOUNT, value))
    }
  }
)

const Form = reduxForm<LoanPaymentFormData, LoanPaymentFormOwnProps>({
  form: FORM_LOAN_PAYMENT
})(LoanPaymentForm)

type FormProps = GetProps<typeof Form>

const Wrapper: React.SFC<FormProps> = ({ ...props }) => {
  return <Form {...props} />
}

export default connector(Wrapper)



// WEBPACK FOOTER //
// ./src/screens/loanPayment/LoanPaymentForm.tsx